import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useCancelUserSubscription } from '../../queries/userSubscriptions';

interface CancelSubscriptionDialogProps {
  subscriptionId: number;
  hasStripeSubscription: boolean;
  onClose: () => void;
}

export function CancelSubscriptionDialog({ subscriptionId, hasStripeSubscription, onClose }: CancelSubscriptionDialogProps) {
  const [type, setType] = useState<'instant' | 'period-end'>('instant');
  const [error, setError] = useState<string | null>(null);
  const cancel = useCancelUserSubscription();

  const handleConfirm = () => {
    setError(null);
    cancel.mutate(
      { id: subscriptionId, type },
      { onSuccess: onClose, onError: (err) => setError(extractErrorMessage(err, 'Cancellation failed.')) },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Cancel subscription</Dialog.Title>

          <div className="mt-4 space-y-2">
            <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
              <input
                type="radio"
                name="cancel-type"
                checked={type === 'instant'}
                onChange={() => setType('instant')}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Cancel immediately</span>
                <br />
                <span className="text-muted-foreground">Access ends now.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
              <input
                type="radio"
                name="cancel-type"
                checked={type === 'period-end'}
                onChange={() => setType('period-end')}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Cancel at period end</span>
                <br />
                <span className="text-muted-foreground">
                  {hasStripeSubscription
                    ? 'Access continues until the current billing period ends.'
                    : 'This subscription has no active billing period, so this ends access immediately, same as above.'}
                </span>
              </span>
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={cancel.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {cancel.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Confirm cancellation
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
